/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useLayoutEffect, useRef, useMemo } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import debounce from 'lodash/debounce';

import { ATL_GENERAL } from '@confluence/web-item-location/entry-points/webFragmentLocations';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { usePageState } from '@confluence/page-context';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState';
import { ADVANCED_SEARCH } from '@confluence/named-routes/entry-points/routes';
import { isPageOrBlogPost } from '@confluence/content-types-utils/entry-points/isPageOrBlogpost';
import {
	superbatchJSRecorder,
	superbatchCSSRecorder,
} from '@confluence/ssr-utilities/entry-points/SuperbatchResourceRecorder';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder';
import { expVal } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations';

import type {
	useATLGeneralWebPanelObserverSpaceThemeQuery as useATLGeneralWebPanelObserverSpaceThemeQuery$data,
	useATLGeneralWebPanelObserverSpaceThemeQueryVariables as useATLGeneralWebPanelObserverSpaceThemeQuery$variables,
} from './__types__/useATLGeneralWebPanelObserverSpaceThemeQuery';
import { useWebPanelLocation } from './useWebPanelLocation';
import {
	presetConnectAppsWrapperDimension,
	removeConnectAppsWrapperDimension,
	MAX_WAIT_TIME_REMOVE_DIMENSION,
} from './breakoutUtils';

export const useATLGeneralWebPanelObserver = (isViewPage: boolean, isEdit?: boolean) => {
	const [{ contentId, spaceKey, contentType }] = usePageState();
	const { panels } = useWebPanelLocation({
		location: ATL_GENERAL,
		fetchPolicy: 'cache-first',
		spaceKey,
		contentId,
	});
	const fixedATLGeneralWebPanelRef = useRef(null);
	const allAppsLoaded = useRef(false);
	const isLivePage = useIsCurrentPageLive();
	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);
	const routeDataRef = useRouteDataRef();
	const renderServerPlaceholder = useRenderServerPlaceholder();
	const presetTimeoutConfig = StatsigConfigurations.getNumber(
		'confluence_webpanel_preset_dimension_time',
		0,
	);
	const heightUpdateThreshold = StatsigConfigurations.getNumber(
		'confluence_webpanel_update_height_threshold',
		0,
	);

	// For Nav 3, we expand atl.general panel to full width when the side nav is not present. This is the case for
	// company hub and when url has mode=global, such as on custom homepage
	// For Nav 4, the side nav is always present, so we have an additional check on isNav4Enabled in FixedWebPanelWrapper
	const isFullWidth =
		isCompanyHubSpaceKey(spaceKey) || routeDataRef.current?.queryParams?.mode === 'global';

	const shouldShowATLGeneralWebPanel = panels.length > 0;

	if (
		shouldShowATLGeneralWebPanel &&
		expVal('confluence_frontend_early_webpanel_initialization', 'isEnabled', false)
	) {
		superbatchJSRecorder.includeConnectWebResources();
		superbatchCSSRecorder.includeConnectWebResources();
	}

	// Determines if panels should not display a UI element and only allow for the support of scripts and analytics
	const shouldHidePanels = !isPageOrBlogPost(contentType) && contentType;

	// Fixed behavior should be consistent for all pages as the same header is used for live / edit as well
	const canFixOnEditOrLivePage = isViewPage || isEdit || isLivePage;

	// If the other conditions for a fixed web panel are true, check whether the space is themed
	// The web panel should not be fixed for themed spaces since they do not have a fixed page header
	const canFixATLGeneralWebPanel = shouldShowATLGeneralWebPanel && canFixOnEditOrLivePage;
	const { data } = useQuery<
		useATLGeneralWebPanelObserverSpaceThemeQuery$data,
		useATLGeneralWebPanelObserverSpaceThemeQuery$variables
	>(
		gql`
			query useATLGeneralWebPanelObserverSpaceThemeQuery($spaceKey: String) {
				space(key: $spaceKey) {
					id
					theme {
						themeKey
					}
				}
			}
		`,
		{
			skip: !canFixATLGeneralWebPanel,
			variables: { spaceKey },
		},
	);

	const isATLGeneralWebPanelFixed = canFixATLGeneralWebPanel && data?.space?.theme === null;
	const isAdvancedSearch = routeDataRef.current?.routeName === ADVANCED_SEARCH.name;

	// ResizeObserver is needed because the height of web panel items can change at any time
	const resizeObserver = useMemo(() => {
		if (!renderServerPlaceholder) {
			const callback = debounce(([entry]) => {
				if (entry) {
					// Remove each connect app preset dimension until all apps are loaded
					if (!allAppsLoaded.current) {
						allAppsLoaded.current = !!removeConnectAppsWrapperDimension(entry.target);
					}

					// Only reset container height after all connect apps are loaded,
					// to prevent layout shift jumping up and down
					if (allAppsLoaded.current) {
						let newHeight = entry.target.clientHeight;
						if (
							Boolean(window.__SSR_RENDERED__) &&
							heightUpdateThreshold > 0 &&
							!isAdvancedSearch &&
							expVal('confluence_remove_atlgeneral_height_timeout', 'isEnabled', false)
						) {
							const initialHeight = window['__ATL_GENERAL_WEBPANEL_INITIAL_HEIGHT__'] || 0;
							const currentHeight = entry.target.clientHeight || 0;
							// Ignore height changes that are less than the threshold
							if (Math.abs(currentHeight - initialHeight) <= heightUpdateThreshold) {
								newHeight = initialHeight;
							}
						}

						document.documentElement.style.setProperty(
							'--atl-general-webpanel-height',
							`${newHeight}px`,
						);
						const detail = expVal(
							'confluence_p2m_style_recalc_and_expand_joint_exp',
							'isEnabled',
							false,
						)
							? { detail: { height: newHeight } }
							: undefined;
						document.dispatchEvent(new CustomEvent('atlGeneralWebPanelHeightUpdated', detail));
					}
				}
			}, 100);
			return new ResizeObserver(callback);
		}
	}, [renderServerPlaceholder, heightUpdateThreshold, isAdvancedSearch]);

	useLayoutEffect(() => {
		const fixedATLGeneralWebPanel = fixedATLGeneralWebPanelRef.current;
		let appLoadTimeoutId;
		let presetTimeoutId;
		if (!renderServerPlaceholder && isATLGeneralWebPanelFixed && fixedATLGeneralWebPanel) {
			// Handle content types that have web panel hidden, some content types have web panel hidden,
			// so it don't need to preset wrapper dimension.
			if (shouldHidePanels) {
				document.documentElement.style.setProperty('--atl-general-webpanel-height', `0px`);
				const detail = expVal(
					'confluence_p2m_style_recalc_and_expand_joint_exp',
					'isEnabled',
					false,
				)
					? { detail: { height: 0 } }
					: undefined;
				document.dispatchEvent(new CustomEvent('atlGeneralWebPanelHeightUpdated', detail));
			} else {
				const totalHeight = presetConnectAppsWrapperDimension(
					fixedATLGeneralWebPanel,
					expVal('confluence_frontend_early_webpanel_initialization', 'isEnabled', false),
				);
				if (totalHeight) {
					document.documentElement.style.setProperty(
						'--atl-general-webpanel-height',
						`${totalHeight}px`,
					);
					const detail = expVal(
						'confluence_p2m_style_recalc_and_expand_joint_exp',
						'isEnabled',
						false,
					)
						? { detail: { height: totalHeight } }
						: undefined;
					document.dispatchEvent(new CustomEvent('atlGeneralWebPanelHeightUpdated', detail));
				}

				// If after max wait time, connect apps are still not loaded, then remove preset dimensions
				appLoadTimeoutId = setTimeout(() => {
					if (!allAppsLoaded.current) {
						removeConnectAppsWrapperDimension(fixedATLGeneralWebPanel, true);
						allAppsLoaded.current = true;
						const connectAppsWrapperContainer = fixedATLGeneralWebPanel as HTMLElement;
						const containerHeight = connectAppsWrapperContainer?.clientHeight || 0;
						document.documentElement.style.setProperty(
							'--atl-general-webpanel-height',
							`${containerHeight}px`,
						);
						const detail = expVal(
							'confluence_p2m_style_recalc_and_expand_joint_exp',
							'isEnabled',
							false,
						)
							? { detail: { height: containerHeight } }
							: undefined;
						document.dispatchEvent(new CustomEvent('atlGeneralWebPanelHeightUpdated', detail));
					}
				}, MAX_WAIT_TIME_REMOVE_DIMENSION);
			}
		}

		if (isATLGeneralWebPanelFixed) {
			// If the page is SSR rendered and the initial height is set,
			// preserve the initial height a little bit longer until apps height become stable
			if (
				Boolean(window.__SSR_RENDERED__) &&
				!isAdvancedSearch &&
				expVal('confluence_remove_atlgeneral_height_timeout', 'isEnabled', false)
			) {
				presetTimeoutId = setTimeout(() => {
					fixedATLGeneralWebPanel && resizeObserver?.observe(fixedATLGeneralWebPanel);
				}, presetTimeoutConfig);
			} else {
				fixedATLGeneralWebPanel && resizeObserver?.observe(fixedATLGeneralWebPanel);
			}
		}

		return () => {
			if (isATLGeneralWebPanelFixed) {
				fixedATLGeneralWebPanel && resizeObserver?.unobserve(fixedATLGeneralWebPanel);
			}
			if (appLoadTimeoutId) {
				clearTimeout(appLoadTimeoutId);
			}
			if (presetTimeoutConfig && presetTimeoutId) {
				clearTimeout(presetTimeoutId);
			}
		};
	}, [
		shouldShowATLGeneralWebPanel,
		isATLGeneralWebPanelFixed,
		resizeObserver,
		renderServerPlaceholder,
		shouldHidePanels,
		presetTimeoutConfig,
		isAdvancedSearch,
	]);

	return {
		shouldShowATLGeneralWebPanel,
		isATLGeneralWebPanelFixed,
		fixedATLGeneralWebPanelRef,
		isCompanyHub,
		isFullWidth,
		shouldHidePanels,
	};
};
